import { useState } from 'react';
import NextImage from 'next/image';
import { useRouter } from 'next/router';
import { Card, Stack, Typography } from '@krakentech/coral';
import styled from 'styled-components';

import {
  FORM_IDS,
  CREATE_CUSTOMER_FEEDBACK_SOURCE,
} from '@/components/Feedback/consts';
import { useAccountNumberContext } from '@/context/AccountNumberContext/withFetch/AccountNumberContext';
import { useViewerId } from '@/hooks/accounts/useViewerId';
import { useCreateCustomerFeedback } from '@/hooks/CHISurvey/useCreateCustomerFeedback';
import { useFeedbackFormsQuery } from '@/hooks/CHISurvey/useFeedbackFormsQuery';
import {
  useSubmitCustomerFeedback,
  CUSTOMER_FEEDBACK_JOURNEY_KEY_OPTIONS,
} from '@/hooks/CHISurvey/useSubmitCustomerFeedback';
import {
  useFeatureFlag,
  useSyncFeatureFlag,
} from '@/hooks/utils/useFeatureFlags';
import { HappyFaceIcon, MehFaceIcon, SadFaceIcon } from '@/public/images';
import { FeatureNames } from '@/types/features';
import { INTERNAL_PATHS } from '@/utils/urls';

type FeedbackType = 'happy' | 'meh' | 'sad';

const Feedback = () => {
  const router = useRouter();
  const { accountNumber } = useAccountNumberContext();
  const { data: viewerId } = useViewerId();
  const { mutate: createFeedback } = useCreateCustomerFeedback();
  const { mutate: submitFeedback } = useSubmitCustomerFeedback({
    journeyKey: CUSTOMER_FEEDBACK_JOURNEY_KEY_OPTIONS.CHI_FEEDBACK,
  });
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackType | null>(
    null
  );

  const chiFeedbackV1Enabled = useFeatureFlag(FeatureNames.ChiFeedbackV1);
  const chiFeedbackV2Enabled = useSyncFeatureFlag(FeatureNames.ChiFeedbackV2);

  const { data: feedbackForms, isLoading: isLoadingForms } =
    useFeedbackFormsQuery(accountNumber);

  const mappedFormsIds: Record<string, string> = {};

  feedbackForms?.customerFeedbackForms.edges.forEach((form) => {
    mappedFormsIds[form.node.name.toLowerCase()] = form.node.id;
  });

  if (!chiFeedbackV1Enabled) {
    return null;
  }

  const handleFeedback = (feedbackType: FeedbackType) => {
    if (chiFeedbackV2Enabled) {
      // new path with additional answer form
      const formId = mappedFormsIds[feedbackType];
      router.push(
        `${INTERNAL_PATHS.CHI.path}?formId=${formId}&feedbackType=${feedbackType}`
      );
    } else if (selectedFeedback === null && accountNumber && viewerId) {
      setSelectedFeedback(feedbackType);

      createFeedback(
        {
          input: {
            accountNumber,
            accountUserId: parseInt(viewerId),
            feedbackSource: CREATE_CUSTOMER_FEEDBACK_SOURCE,
          },
        },
        {
          onSuccess: (response: any) => {
            const feedbackId =
              response?.createCustomerFeedback?.customerFeedback?.id;

            if (feedbackId) {
              submitFeedback({
                input: {
                  formId: FORM_IDS[feedbackType],
                  feedbackId: parseInt(feedbackId),
                  accountNumber,
                  issueResolved: feedbackType === 'happy',
                  answer: `Reward feedback: ${feedbackType}`,
                },
              });
            }
          },
        }
      );
    }
  };

  const isDisabled =
    !!selectedFeedback || (chiFeedbackV2Enabled && isLoadingForms); // All buttons are disabled from clicks once any feedback is selected, or while loading forms in V2

  const isVisuallyDisabled = (type: FeedbackType) =>
    Boolean(selectedFeedback && selectedFeedback !== type); // Only non-selected buttons look disabled

  return (
    <Card fullWidth>
      <Stack direction="vertical" gap="smMd" alignItems="center">
        <Typography variant="h2" textAlign="center">
          How did we do today?
        </Typography>
        <Stack gap="smMd" justifyContent="center">
          <StyledIconButton
            type="button"
            onClick={() => handleFeedback('sad')}
            aria-label="Sad"
            selected={selectedFeedback === 'sad'}
            disabled={isDisabled}
            visuallyDisabled={isVisuallyDisabled('sad')}
          >
            <NextImage
              src={SadFaceIcon}
              alt=""
              height={60}
              width={60}
              style={{ width: 'auto', height: 'auto' }}
            />
          </StyledIconButton>
          <StyledIconButton
            type="button"
            onClick={() => handleFeedback('meh')}
            aria-label="Neutral"
            selected={selectedFeedback === 'meh'}
            disabled={isDisabled}
            visuallyDisabled={isVisuallyDisabled('meh')}
          >
            <NextImage
              src={MehFaceIcon}
              alt=""
              height={60}
              width={60}
              style={{ width: 'auto', height: 'auto' }}
            />
          </StyledIconButton>
          <StyledIconButton
            type="button"
            onClick={() => handleFeedback('happy')}
            aria-label="Happy"
            selected={selectedFeedback === 'happy'}
            disabled={isDisabled}
            visuallyDisabled={isVisuallyDisabled('happy')}
          >
            <NextImage
              src={HappyFaceIcon}
              alt=""
              height={60}
              width={60}
              style={{ width: 'auto', height: 'auto' }}
            />
          </StyledIconButton>
        </Stack>
      </Stack>
    </Card>
  );
};

export default Feedback;

const StyledIconButton = styled.button<{
  selected: boolean;
  disabled?: boolean;
  visuallyDisabled: boolean;
}>`
  border-radius: 50%;
  padding: 0;
  min-width: 60px;
  min-height: 60px;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid
    ${({ selected, visuallyDisabled }) => {
      if (visuallyDisabled) return '#e0e0e0';
      return selected ? '#1976d2' : 'transparent';
    }};
  background: ${({ selected, visuallyDisabled }) => {
    if (visuallyDisabled) return '#f5f5f5';
    return selected ? 'black' : 'transparent';
  }};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition:
    border 0.2s,
    background 0.2s;
  transition: 0.5s ease all;
  opacity: ${({ visuallyDisabled }) => (visuallyDisabled ? 0.5 : 1)};
  &:hover {
    transform: scale(1.1);
  }
`;
